import { ChangeDetectionStrategy, Component, effect, inject, signal, WritableSignal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { NbDateService } from '@nebular/theme';
import { BuildingFilterDto, TenantDetailDto, TenantItemDto, TenantService } from '@artemis-software/wr-api';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { DateRangeObject } from '@models/date-range';
import { closeOpenEnd } from '@/utils/filter-range-utils';

export interface ExternalTenantStatisticsFilter {
  tenantId?: string;
  selectedRange?: DateRangeObject;
}

@Component({
  standalone: false,
  selector: 'wr-cooperative-statistics-page',
  templateUrl: './tenant-statistics-page.component.html',
  styleUrls: ['./tenant-statistics-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TenantStatisticsPageComponent {

  private readonly formBuilder = inject(FormBuilder);
  private readonly dateService = inject<NbDateService<Date>>(NbDateService);
  private readonly tenantService = inject(TenantService);

  private readonly defaultRange = this.initialRange();

  readonly filter: WritableSignal<ExternalTenantStatisticsFilter> = signal({
    tenantId: undefined,
    selectedRange: closeOpenEnd(this.defaultRange),
  });

  form!: FormGroup;

  readonly externalTenants = asyncComputed(() => this.tenantService.findAll({ type: TenantItemDto.TypeEnum.External }));

  constructor() {
    this.initFilterForm();

    effect(() => {
      const externalTenants = this.externalTenants();
      if (
        externalTenants &&
        externalTenants.length > 0 &&
        this.form.controls['tenantId'].value !== externalTenants[0].id
      ) {
        this.form.controls['tenantId'].setValue(externalTenants[0].id);
        this.filter.set({
          ...this.filter(),
          tenantId: externalTenants[0].id
        });
      }
    });
  }

  private initialRange(): DateRangeObject {
    const startOfMonth = this.dateService.getMonthStart(this.dateService.today());
    return {
      start: startOfMonth,
      end: this.dateService.getMonthEnd(this.dateService.addMonth(startOfMonth, 1)),
    };
  }

  initFilterForm(): void {
    this.form = this.formBuilder.group({
      tenantId: this.formBuilder.control(null),
      selectedRange: new FormControl(this.defaultRange),
    });
  }

  triggerFilter(): void {
    const formValue = this.form.value;

    this.filter.set({
      ...this.filter(),
      tenantId: formValue.tenantId,
      selectedRange: closeOpenEnd(formValue.selectedRange),
    });
  }

  protected readonly BuildingFilterDto = BuildingFilterDto;
  protected readonly TenantTypeEnum = TenantDetailDto.TypeEnum;
}
